import { clearAllAuthCookies } from '~/composables/useCookieManager';
import {
  isTokenExpiring,
  performTokenRefresh,
} from '~/composables/useTokenRefresh';

/**
 * Global Token Refresh Middleware
 *
 * Proactively checks token expiry on every route navigation and refreshes
 * the token before it expires. This provides a seamless user experience
 * by preventing auth redirects due to expired tokens.
 *
 * Features:
 * - Runs on client-side only
 * - Checks token 2 minutes before actual expiry
 * - Uses singleton pattern to prevent multiple simultaneous refreshes
 * - Falls back to login redirect only if refresh fails
 */
export default defineNuxtRouteMiddleware(async to => {
  // Only run on client-side
  if (import.meta.server) return;

  // Skip for auth-related routes to prevent loops
  const authRoutes = ['/login', '/logout', '/forgot-password', '/register'];
  if (authRoutes.some(route => to.path.startsWith(route))) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const auth = useAuth() as any;

  // Skip if user is not logged in
  if (!auth.loggedIn) return;

  // Get the current access token
  const token = auth.strategy.token.get();

  // If no token exists, let other middleware handle it
  if (!token || token === 'false') return;

  // Check if token is expiring soon (within 2 minutes)
  if (isTokenExpiring(token, 120)) {
    const refreshSuccess = await performTokenRefresh();

    if (!refreshSuccess) {
      // Refresh failed - clear cookies and redirect to login
      clearAllAuthCookies();

      // Reset auth state
      auth.strategy.token.set(false);
      auth.strategy.refreshToken.set(false);

      return navigateTo('/login');
    }
  }
});
